import React, { useEffect } from 'react';
import { IS_PRERENDER } from '../utils/prerender';
import { createPortal } from 'react-dom';
import { useT, LANGS } from '../i18n';
import './LanguageModal.css';

// Country flags rather than language names alone. A first-time visitor is
// looking at a site in a language they may not read, so the fastest thing to
// recognise is a flag and the language's name IN THAT LANGUAGE — never a
// translated one, which would be the very thing they cannot read.
const FLAGS = { en: '🇺🇸', ru: '🇷🇺', hy: '🇦🇲' };

// ── THE LANGUAGE PICKER ──────────────────────────────────────────────────────
// One component, two jobs:
//
//   FIRST VISIT (`dismissible={false}`) — nobody has chosen yet, so the site is
//   showing a default rather than a preference. There is no ✕ and the backdrop
//   does not close it: a dismissed prompt leaves the visitor reading a language
//   they did not pick with no obvious way to change it, which is exactly the
//   state it exists to prevent. It costs one tap and never appears again.
//
//   ON DEMAND (`dismissible`) — opened from the gear beside the sign-up button,
//   where it replaced three EN/RU/AM buttons sitting permanently in the header.
//
// NO TITLE, deliberately. Any heading would have to be written in one language,
// which is the one thing this dialog cannot assume. The logo says whose site it
// is and three flags say what the choice is; nothing else is needed.
export default function LanguageModal({ onClose, dismissible = true }) {
  const { setLang, lang } = useT();

  // Escape closes — but only when there is something to close back to.
  useEffect(() => {
    if (!dismissible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismissible, onClose]);

  const pick = (code) => {
    setLang(code);
    onClose();
  };

  // Not during the prerender capture — a portal lands outside #root and would
  // survive as dead markup over the live one. See utils/prerender.
  if (IS_PRERENDER) return null;

  return createPortal(
    <div
      className="lgm-overlay"
      onClick={dismissible ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Language"
    >
      <div className="lgm-box" onClick={e => e.stopPropagation()}>
        {dismissible && (
          <button className="lgm-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        )}

        <img src="/images/Tunify Original logo tab.png" alt="Tunify" className="lgm-logo" />

        <div className="lgm-grid">
          {LANGS.map(l => (
            <button
              key={l.code}
              type="button"
              className={`lgm-card${l.code === lang ? ' lgm-card--on' : ''}`}
              onClick={() => pick(l.code)}
              // The label is the language's own name, so it is meaningful to
              // the person who needs it whatever the site is currently set to.
              aria-label={l.name}
            >
              <span className="lgm-flag" aria-hidden="true">{FLAGS[l.code]}</span>
              <span className="lgm-name">{l.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
